/**
 * MELLY collection page behaviour.
 * - <melly-filter-drawer>: on phones/tablets the filter panel is a drawer opened by the "סינון"
 *   button (data-melly-filter-open); it closes on its close button, the backdrop or Escape, locks
 *   page scrolling while open and returns focus to the button.
 * - Filter and sort changes submit the (GET) filter form right away on desktop; inside the drawer the
 *   customer applies them with the "הצגת תוצאות" button. Sorting always applies immediately.
 * The form works without JavaScript (plain GET submit).
 */
const DESKTOP = window.matchMedia('(min-width: 990px)');

class MellyFilterDrawer extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {HTMLElement | null} */
  #opener = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const form = this.querySelector('form');

    document.addEventListener('click', (event) => {
      const target = /** @type {Element} */ (event.target);
      const opener = target.closest?.('[data-melly-filter-open]');
      if (opener && opener.getAttribute('aria-controls') === this.id) {
        this.#opener = /** @type {HTMLElement} */ (opener);
        this.open();
      }
    }, { signal });

    this.addEventListener('click', (event) => {
      const target = /** @type {Element} */ (event.target);
      if (target === this || target.closest('[data-melly-filter-close]')) this.close();
    }, { signal });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
    }, { signal });

    // Auto-submit: sort always; filters on desktop only (the drawer has an apply button)
    document.addEventListener('change', (event) => {
      const field = /** @type {HTMLInputElement | HTMLSelectElement} */ (event.target);
      if (!form || field.form !== form) return;
      const isSort = field.name === 'sort_by';
      if (isSort || DESKTOP.matches) form.requestSubmit();
    }, { signal });

    // Leaving the drawer layout (rotating to desktop) must not leave the page scroll-locked
    DESKTOP.addEventListener('change', () => { if (DESKTOP.matches) this.close(); }, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#unlock();
  }

  open() {
    if (DESKTOP.matches) return;
    this.setAttribute('open', '');
    this.#opener?.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
    const panel = /** @type {HTMLElement | null} */ (this.querySelector('[role="dialog"]'));
    panel?.setAttribute('aria-modal', 'true');
    panel?.focus();
  }

  close() {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    this.querySelector('[role="dialog"]')?.setAttribute('aria-modal', 'false');
    this.#opener?.setAttribute('aria-expanded', 'false');
    this.#unlock();
    this.#opener?.focus();
  }

  #unlock() {
    document.documentElement.style.removeProperty('overflow');
  }
}

if (!customElements.get('melly-filter-drawer')) {
  customElements.define('melly-filter-drawer', MellyFilterDrawer);
}
